/**
 * Pure Markdown-string normalization applied to the editor's output before it
 * reaches `onChange`. Kept free of React/Lexical so it can be unit-tested in
 * isolation (see __tests__/markdownRoundtrip.test.ts).
 */

// Lexical's exporter defensively escapes EVERY `* _ ` ~` in text content
// (`\*`, `\_`, …) so its own importer round-trips. That's "Lexical practice",
// not standard CommonMark — the database stores Markdown that non-Lexical
// viewers and external tooling read, so we strip those escapes back to clean
// Markdown on the way out. Because incoming content is already clean Markdown,
// every backslash Lexical emits was one it ADDED to a literal character, so
// removing it reproduces the original.
//
// Safe-guard: a line-LEADING `\*` or `\~` (first non-whitespace on the line)
// must stay escaped — unescaping it would let a standard parser re-read the
// line as a bullet (`* `) or a strikethrough fence. Mid-line occurrences, and
// `_`/backtick anywhere, are safe to unescape (they can't start a block).
export function unescapeStandardMarkdown(markdown: string): string {
  return markdown.replace(/\\([*_`~])/g, (match, char: string, offset: number) => {
    if (char === '*' || char === '~') {
      // Keep the escape only when this `\` is the first non-whitespace on its
      // line (everything from the line start to here is whitespace) — there an
      // unescaped `*`/`~` would be read as a bullet or strikethrough fence.
      const lineStart = markdown.lastIndexOf('\n', offset - 1) + 1;
      if (/^[ \t]*$/.test(markdown.slice(lineStart, offset))) {
        return match;
      }
    }
    return char;
  });
}

// Strip zero-width chars (ZWSP, ZWNJ, ZWJ, BOM) that Lexical can insert, trim
// trailing newlines, and unescape Lexical's defensive Markdown escaping. We
// deliberately do NOT collapse interior blank lines — the editor uses
// shouldPreserveNewLines so intentional blank lines round-trip.
export function normalizeMarkdownOutput(markdown: string): string {
  return unescapeStandardMarkdown(markdown)
    .replace(/[​‌‍﻿]/g, '')
    .replace(/\n+$/, '');
}
